import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { Classroom360Service } from '../../classroom360/classroom360.service';
import { LoginService } from '../../shared-modules/login/login.service';
import { Student360Service } from '../../student360/student360.service';
import { StudentAlerts } from '../models/student-alerts.model';
import { UserPreferencesService } from '../user-preferences/user-preferences.service';
import { UserService } from './../user/user.service';
import { AlertsDialogComponent } from './alerts-dialog/alerts-dialog.component';

@Component({
  selector: 'student-alerts',
  templateUrl: './student-alerts.component.html',
  styleUrls: ['./student-alerts.component.css']
})
export class StudentAlertsComponent implements OnInit, OnChanges {
  @Input() alerts: StudentAlerts[];
  @Input() alertHeight = 20;
  @Input() distanceBetween = 2; // This is the distance between each icon
  @Input() collapsed = false;   // If this is set to true, it will display an icon at most once
  @Input() showAllAlerts = false;
  @Input() addEllipsis = false;
  @Input() setMaxAlertsForcefully = 0;
  @Input() fromStudentSearch = false;

  isV10: boolean;
  hasMedicalAlerts: boolean;
  maxAlerts: number;
  hasIepAlerts: boolean;
  hasReleaseAlerts: boolean;
  hasFamilyAlerts: boolean;
  hasProgramsAndServicesAlerts: boolean;
  hasClassroomAlerts: boolean;
  hasSpecialServices504: boolean;
  displayedAlerts: StudentAlerts[] = [];
  programsAndServicesAlerts: StudentAlerts[];
  userHasPermission = false;
  userType: number;
  showEllipsis = false;
  currentDate = new Date();
  useSection504 = false;

  constructor(
    private loginService: LoginService,
    public domSanitizer: DomSanitizer,
    public dialog: MatDialog,
    private userPreferences: UserPreferencesService,
    private userService: UserService,
    private router: Router,
    private parentPortalService: Student360Service,
    private classroom360Service: Classroom360Service
  ) {
    this.isV10 = this.loginService.LoginSettings.V10;
    this.userType = this.userService.currentUser.UserType;
    if (this.showAllAlerts == false && this.userPreferences.generalPreferences && this.userPreferences.generalPreferences.MaximumAlertIcon != 'All') {
      this.maxAlerts = parseInt(this.userPreferences.generalPreferences.MaximumAlertIcon, 10);
    } else {
      this.maxAlerts = 0;
    }
  }

  ngOnInit() {
    this.useSection504 = this.loginService.LoginSettings.UseSection504;
    this.userHasPermission = this.userType == 1 || this.alerts.find(alert => alert.UserHasPermisison) != null || this.alerts.find(alert => alert.AlertType == 'IEP') != null;
    this.processAlerts();
  }

  ngOnChanges() {
    this.userHasPermission = this.userType == 1 || this.alerts.find(alert => alert.UserHasPermisison) != null || this.alerts.find(alert => alert.AlertType == 'IEP') != null;
    this.processAlerts();
  }

  processAlerts() {
    // POST: Determes which alerts to show
    this.displayedAlerts = [];

    // If the icons are collapsed, show one of each
    if (this.collapsed) {
      const alertTypesCovered: string[] = [];
      const alertIconsCovered: number[][] = [];
      for (let i = 0; i < this.alerts.length; i++) {
        const alert = this.alerts[i];
        if (alert.AlertType == 'Programs And Services Alert') {
          if (alert.AlertIcon && !alertIconsCovered.includes(alert.AlertIcon)) {

            if (moment(this.currentDate).isBefore(alert.EndDate) || (moment(alert.EndDate, 'YYYY-MM-DDThh:mm:ss').format('MM/DD/YY') == '01/01/01')) {
              this.displayedAlerts.push(alert);
              alertIconsCovered.push(alert.AlertIcon);
            }
          }
          else if (alert.Program == '504' && this.useSection504 == true) {
            this.displayedAlerts.push(alert);
          }
        }
        else {
          if (alert.AlertType && !alertTypesCovered.includes(alert.AlertType)) {
            if (alert.AlertType == 'IEP') {
              if ((this.userType == 1 && this.classroom360Service.staffAssignedPortals && this.classroom360Service.staffAssignedPortals.filter(o => o.PortalDescription == '/iepAlerts').length > 0)
                || (this.userType != 1 && this.parentPortalService.parentAssignedPortals && this.parentPortalService.parentAssignedPortals.filter(o => o.PortalDescription == '/iepAlerts').length > 0)) {
                alert.UserHasPermisison = true;
              }
            }
            this.displayedAlerts.push(alert);
            alertTypesCovered.push(alert.AlertType);
          }
        }
      }
    }
    // If not, display them according to some heuristics
    else {
      // Check for IEP Alerts
      if (this.alerts.find(alert => alert.AlertType == 'IEP')) {
        const iepAlerts = this.alerts.filter(alert => alert.AlertType == 'IEP');
        // Set User has permission flag
        if ((this.userType == 1 && this.classroom360Service.staffAssignedPortals && this.classroom360Service.staffAssignedPortals.filter(o => o.PortalDescription == '/iepAlerts').length > 0)
          || (this.userType != 1 && this.parentPortalService.parentAssignedPortals && this.parentPortalService.parentAssignedPortals.filter(o => o.PortalDescription == '/iepAlerts').length > 0)) {
          iepAlerts[0].UserHasPermisison = true;
        }

        // For display purposes we only want 1 of this type
        this.displayedAlerts.push(iepAlerts[0]);
        this.hasIepAlerts = true;
      }
      // Check for medical Alerts
      if (this.alerts.find(alert => alert.AlertType == 'Medical Alert')) {
        const medicalAlerts = this.alerts.filter(alert => alert.AlertType == 'Medical Alert');
        // For display purposes we only want 1 of this type
        this.displayedAlerts.push(medicalAlerts[0]);
        this.hasMedicalAlerts = true;
      }
      // Check for medical Restriction Alerts
      if (this.alerts.find(alert => alert.AlertType == 'Medical Restriction Alert')) {
        const medicalRestrictionAlerts = this.alerts.filter(alert => alert.AlertType == 'Medical Restriction Alert');
        // For display purposes we only want 1 of this type
        this.displayedAlerts.push(medicalRestrictionAlerts[0]);
        this.hasMedicalAlerts = true;
      }
      // Check for family Alerts
      if (this.alerts.find(alert => alert.AlertType == 'Family Alert')) {
        const familyAlerts = this.alerts.filter(alert => alert.AlertType == 'Family Alert');
        this.displayedAlerts.push(familyAlerts[0]);
        this.hasFamilyAlerts = true;
      }
      // Check for release Alerts
      if (this.alerts.find(alert => alert.AlertType == 'Release Alert')) {
        const releaseAlerts = this.alerts.filter(alert => alert.AlertType == 'Release Alert');
        // For display purposes we only want 1 of this type
        this.displayedAlerts.push(releaseAlerts[0]);
        this.hasReleaseAlerts = true;
      }
      // Check for Program and Services Alerts
      if (this.alerts.find(alert => alert.AlertType == 'Programs And Services Alert')) {
        this.hasProgramsAndServicesAlerts = true;
        this.programsAndServicesAlerts = this.alerts.filter(alert => alert.AlertType == 'Programs And Services Alert');
        this.displayedAlerts = this.displayedAlerts.concat(this.alerts.filter(alert => alert.AlertType == 'Programs And Services Alert'));
      }
      // Check for Classroom Alerts
      if (this.alerts.find(alert => alert.AlertType == 'Classroom Alert')) {
        // For display purposes we only want 1 of this type
        const classroomAlerts = this.alerts.filter(alert => alert.AlertType == 'Classroom Alert');
        this.displayedAlerts.push(classroomAlerts[0]);
        this.hasClassroomAlerts = true;
      }
      // Check for 504
      if (this.alerts.find(alert => alert.AlertType == '504 Special Service')) {
        // For display purposes we only want 1 of this type
        const specialServices504 = this.alerts.filter(alert => alert.AlertType == '504 Special Service');
        this.displayedAlerts.push(specialServices504[0]);
        this.hasSpecialServices504 = true;
      }
    }

    if (this.fromStudentSearch && !this.isV10) {
      const tmpDisplayedAlerts = this.displayedAlerts;
      this.displayedAlerts = [];
      tmpDisplayedAlerts.forEach(element => {

        const isDuplicate = this.displayedAlerts.find(a => a.AlertType == element.AlertType && a.Program == element.Program && a.Service == element.Service);
        if (!isDuplicate) {
          this.displayedAlerts.push(element);
        }
      });
    }

    // Cut down the displayedAlerts array to the user preference
    if (this.setMaxAlertsForcefully > 0) {
      if (this.addEllipsis && this.displayedAlerts.length > this.setMaxAlertsForcefully) {
        this.showEllipsis = true;
      }
      this.displayedAlerts = this.displayedAlerts.slice(0, this.setMaxAlertsForcefully);
    }
    else if (this.showAllAlerts == false && this.maxAlerts > 0) {
      if (this.addEllipsis && this.displayedAlerts.length > this.maxAlerts) {
        this.showEllipsis = true;
      }
      this.displayedAlerts = this.displayedAlerts.slice(0, this.maxAlerts);
    }
  }

  studentAlertsClick(alertType: string) {
    if (this.userHasPermission) {
      this.dialog.open(AlertsDialogComponent, {
        width: '800px',
        maxHeight: '80vh',
        data: this.alerts
      });
    }
  }
}
